// © 2026 Holmes Computer Consultants. All rights reserved.
import { BufferAttribute, BufferGeometry, Group, LOD, Mesh, MeshStandardMaterial, type Material } from 'three'
import type { CabinetDimensions } from './cabinet'
import { bodyLengthFor, cabinetBottomY } from './cabinet'
import { FINISHES } from './partsKit'
import { addLegFloorContact } from './floorContact'

type LegGroup = { role: string; positions: number[]; normals: number[]; uvs: number[]; triangles: number[] }
type LegSource = { schema: number; levels: { level: string; groups: LegGroup[] }[] }
let source: LegSource | undefined
export async function prepareCabinetLegs(): Promise<void> {
  source ??= (await import('../../SourceArt/CabinetLegs/mesh.json')).default
}

export function cabinetLegsReady(dims: CabinetDimensions): boolean {
  return source !== undefined && Number.isFinite(cabinetBottomY(dims)) && Number.isFinite(dims.width)
    && Number.isFinite(bodyLengthFor(dims)) && cabinetBottomY(dims) >= .150 && dims.width >= .250 && bodyLengthFor(dims) >= .350
}

/** Shared nominal hardware is translated/reflected, preserving foot gauge and floor contact. */
export function fitCabinetLegs(group: LegGroup, dims: CabinetDimensions) {
  const positions: number[] = [], normals: number[] = [], uvs: number[] = [], triangles: number[] = []
  const bottom = cabinetBottomY(dims), join = .7235 - .032, fixed = .060
  const scale = (bottom - .032 - fixed) / (join - fixed)
  for (const sx of [-1, 1]) for (const sz of [-1, 1]) {
    const first = positions.length / 3, x = sx * (dims.width / 2 - .034), z = sz * (bodyLengthFor(dims) / 2 - .034)
    for (let v = 0; v < group.positions.length; v += 3) {
      const px = group.positions[v]!, py = group.positions[v + 1]!, pz = group.positions[v + 2]!
      const nx = group.normals[v]!, ny = group.normals[v + 1]!, nz = group.normals[v + 2]!
      const y = py <= fixed ? py : py >= join ? py + bottom - .7235 : fixed + (py - fixed) * scale
      const yy = py > fixed && py < join ? ny / scale : ny, length = Math.hypot(nx, yy, nz)
      positions.push(x + px * sx, y, z + pz * sz); normals.push(nx * sx / length, yy / length, nz * sz / length)
      uvs.push(Math.abs(nx) >= Math.abs(nz) ? pz : px, py)
    }
    for (let i = 0; i < group.triangles.length; i += 3) {
      const a = first + group.triangles[i]!, b = first + group.triangles[i + 1]!, c = first + group.triangles[i + 2]!
      triangles.push(a, sx * sz < 0 ? c : b, sx * sz < 0 ? b : c)
    }
  }
  return { positions, normals, uvs, triangles }
}

/** Nine bounded static meshes; only one three-material level is visible. */
export function addCabinetLegs(dims: CabinetDimensions, parent: Group, geometries: BufferGeometry[], materials: Material[]): boolean {
  if (!source || !cabinetLegsReady(dims)) return false
  const finishes = [
    legSteel('legSatinSteel'), legSteel('legFastenerSteel'),
    new MeshStandardMaterial({ ...FINISHES.legFootRubber, name: 'cabinet.leg.rubber' }),
  ]
  materials.push(...finishes)
  const detail = new LOD(), centre = (cabinetBottomY(dims) + .100) / 2; detail.name = 'cabinet.leg.detail'; detail.position.y = centre
  for (let l = 0; l < source.levels.length; l++) {
    const level = source.levels[l]!, built = new Group(); built.name = 'cabinet.leg.' + level.level; built.position.y = -centre
    built.userData['cabinetLegLevel'] = true
    built.visible = l === 0
    for (let role = 0; role < level.groups.length; role++) {
      const g = level.groups[role]!, fitted = fitCabinetLegs(g, dims), geometry = new BufferGeometry()
      geometry.setAttribute('position', new BufferAttribute(new Float32Array(fitted.positions), 3))
      geometry.setAttribute('normal', new BufferAttribute(new Float32Array(fitted.normals), 3))
      geometry.setAttribute('uv', new BufferAttribute(new Float32Array(fitted.uvs), 2)); geometry.setIndex(fitted.triangles)
      geometry.computeBoundingBox(); geometry.computeBoundingSphere(); geometries.push(geometry)
      const mesh = new Mesh(geometry, finishes[role]); mesh.name = 'cabinet.leg.' + level.level + '.' + g.role; built.add(mesh)
    }
    detail.addLevel(built, [0, 1, 2.8][l]!, .10)
  }
  parent.add(detail); addLegFloorContact(dims,parent,geometries,materials); return true
}

function legSteel(role: 'legSatinSteel'|'legFastenerSteel'): MeshStandardMaterial {
  const material = new MeshStandardMaterial({ ...FINISHES[role], name: 'cabinet.leg.'+role, envMapIntensity: .78 })
  // The flats/cup/thread hardware keeps the source's smooth manufactured finish.
  if (role === 'legFastenerSteel') return material
  material.onBeforeCompile = shader => {
    shader.vertexShader = shader.vertexShader.replace('#include <common>','#include <common>\nvarying vec2 vLegUv;').replace('#include <uv_vertex>','#include <uv_vertex>\nvLegUv=uv;')
    shader.fragmentShader = shader.fragmentShader.replace('#include <common>','#include <common>\nvarying vec2 vLegUv;').replace('#include <roughnessmap_fragment>','#include <roughnessmap_fragment>\nfloat legPhase=vLegUv.x*60000.0+sin(vLegUv.x*37471.0)*.9;\nroughnessFactor=clamp(roughnessFactor+sin(legPhase)*.004*exp(-pow(fwidth(legPhase),2.0)/24.0),.16,.90);')
  }
  material.customProgramCacheKey = () => 'cabinet-leg-steel11-finish02'
  return material
}
